import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  ShoppingBag, DollarSign, Package, CheckCircle, XCircle,
  Truck, Search, RefreshCw, Eye, Calendar, Phone, Mail, MapPin, User, LogOut, ShoppingCart, Crown,
} from "lucide-react";
import { format, parseISO, subDays, isWithinInterval } from "date-fns";

// Helper function to format delivery date from yyyy-MM-dd to dd-MM-yyyy
const formatDeliveryDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    // Handle yyyy-MM-dd format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split("-");
      return `${day}-${month}-${year}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

// Store tag component
const StoreTag = ({ tag }) => {
  const colors = {
    W: "bg-green-100 text-green-700 border-green-200",
    C: "bg-red-100 text-red-700 border-red-200",
    WH: "bg-blue-100 text-blue-700 border-blue-200",
  };
  const labels = { W: "Woolworths", C: "Coles", WH: "ChemistWH" };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border ${colors[tag] || "bg-gray-100 text-gray-600 border-gray-200"}`}
      title={labels[tag] || "Unknown"}
    >
      {tag}
    </span>
  );
};

// Status badge
const StatusBadge = ({ status }) => {
  const styles = {
    delivered: "bg-emerald-100 text-emerald-700 border-emerald-200",
    pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
    confirmed: "bg-blue-100 text-blue-700 border-blue-200",
    ready_for_delivery: "bg-orange-100 text-orange-700 border-orange-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
      {status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
};

// Member badge
const MemberBadge = ({ isMember }) => (
  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold ${isMember ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>
    {isMember ? <><Crown className="w-2.5 h-2.5 mr-0.5" />Member</> : "Guest"}
  </span>
);

// Payment badge
const PaymentBadge = ({ status, transactionId }) => (
  <div className="flex flex-col">
    <span className={`text-[10px] font-medium ${status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>
      {status === "paid" ? "✓ Paid" : status || "Pending"}
    </span>
    {transactionId && <span className="text-[9px] text-slate-400 font-mono">{transactionId.slice(0, 10)}...</span>}
  </div>
);

const DriverPortal = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isDriver, setIsDriver] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({ 
    status: "all", 
    search: "",
    dateRange: "all",
    startDate: "",
    endDate: ""
  });
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Driver allowed statuses only
  const driverStatuses = ["delivered"];

  // Check driver access
  useEffect(() => {
    const checkAccess = async () => {
      if (!token) {
        // Wait a bit for token to load from localStorage
        await new Promise(resolve => setTimeout(resolve, 500));
        if (!token) { 
          setAccessError("Please login to access the Driver Portal"); 
          setLoading(false); 
        }
        return;
      }
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/founders/check`, { headers });
        const { is_driver, is_founder } = response.data;
        
        if (is_driver || is_founder) {
          setIsDriver(true);
          setAccessError(null);
          fetchDriverOrders();
        } else { 
          setIsDriver(false);
          setAccessError("Access restricted to BundleCart drivers"); 
          setLoading(false); 
        }
      } catch (error) {
        console.error("Access check error:", error);
        if (error.response?.status === 401) {
          setAccessError("Session expired. Please login again.");
        } else {
          setAccessError("Unable to verify access"); 
        }
        setLoading(false);
      }
    };
    
    // Small delay to allow auth context to initialize
    const timer = setTimeout(checkAccess, 100);
    return () => clearTimeout(timer);
  }, [token]);

  // Fetch driver orders
  const fetchDriverOrders = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/driver/orders`, { headers });
      setOrders(response.data.orders || []);
    } catch (error) {
      if (error.response?.status === 403) { 
        setAccessError("Driver access required"); 
      } else {
        toast.error("Failed to load orders");
      }
    } finally { 
      setLoading(false); 
    }
  };

  // Update order status - using PUT instead of PATCH
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/orders/${orderId}/status`, 
        { status: newStatus }, 
        { headers }
      );
      toast.success(`Status updated to ${newStatus.replace(/_/g, " ")}`);
      fetchDriverOrders();
      if (selectedOrder) {
        setSelectedOrder({ ...selectedOrder, order_status: newStatus });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Handle date range change
  const handleDateRangeChange = (value) => {
    const now = new Date();
    let startDate = "";
    let endDate = "";
    
    if (value === "today") {
      startDate = format(now, "yyyy-MM-dd");
      endDate = format(now, "yyyy-MM-dd");
    } else if (value === "7days") {
      startDate = format(subDays(now, 7), "yyyy-MM-dd");
      endDate = format(now, "yyyy-MM-dd");
    } else if (value === "30days") {
      startDate = format(subDays(now, 30), "yyyy-MM-dd");
      endDate = format(now, "yyyy-MM-dd");
    } else if (value === "90days") {
      startDate = format(subDays(now, 90), "yyyy-MM-dd");
      endDate = format(now, "yyyy-MM-dd");
    }
    
    setFilters({ ...filters, dateRange: value, startDate, endDate });
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    // Status filter
    const matchesStatus = filters.status === "all" || order.order_status === filters.status;
    
    // Search filter
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = !filters.search || 
      (order.order_number || "").toLowerCase().includes(searchLower) ||
      (order.delivery_details?.suburb || "").toLowerCase().includes(searchLower) ||
      (order.delivery_details?.address || "").toLowerCase().includes(searchLower) ||
      (order.customer_name || "").toLowerCase().includes(searchLower);
    
    // Date filter
    let matchesDate = true;
    if (filters.startDate && filters.endDate && order.created_at) {
      try {
        const orderDate = parseISO(order.created_at);
        const start = new Date(filters.startDate);
        const end = new Date(filters.endDate);
        end.setHours(23, 59, 59, 999);
        matchesDate = isWithinInterval(orderDate, { start, end });
      } catch {
        matchesDate = true;
      }
    }
    
    return matchesStatus && matchesSearch && matchesDate;
  });

  // Open order detail
  const openOrderDetail = (order) => {
    setSelectedOrder(order);
  };

  // Access denied screen
  if (accessError || (!loading && !isDriver)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <Truck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Driver Access Required</h1>
          <p className="text-slate-600 mb-6">{accessError || "This portal is restricted to BundleCart drivers."}</p>
          <Button onClick={() => navigate("/")} variant="outline">Return to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-50">
        {/* Custom Driver Header with BundleCart Branding */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* BundleCart Logo & Branding */}
              <Link to="/driver" className="flex items-center gap-2">
                <div className="w-9 h-9 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center shadow-sm">
                  <ShoppingCart className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-lg text-slate-900 font-['Outfit'] leading-none">BundleCart</span>
                  <span className="text-[10px] text-cyan-600 font-medium">Driver Portal</span>
                </div>
              </Link>
            </div>

            {/* Account Menu - Only Logout */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="driver-account-menu">
                  <User className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="driver-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="max-w-7xl mx-auto p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-5 h-5 text-cyan-600" />
                <span className="text-xs font-medium text-cyan-600 uppercase tracking-wider">Driver Portal</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">My Deliveries</h1>
            </div>
            <Button onClick={fetchDriverOrders} variant="outline" size="sm" disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>

          {/* Filters - Same as Founder Portal */}
          <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-200 mb-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search order, suburb, address..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10 h-9 text-sm" data-testid="search-input"
                  />
                </div>
              </div>
              
              {/* Status Filter - Ready for Delivery and Delivered */}
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ready_for_delivery">Ready for Delivery</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Range Filter - Same as Founder Portal */}
              <Select value={filters.dateRange} onValueChange={handleDateRangeChange}>
                <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="date-filter">
                  <Calendar className="w-3 h-3 mr-1" /><SelectValue placeholder="Date Range" />
                </SelectTrigger>
                <SelectContent>
                  {[["all", "All Time"], ["today", "Today"], ["7days", "7 Days"], ["30days", "30 Days"], ["90days", "90 Days"]].map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Custom Date Range - dd/mm/yyyy format */}
              <div className="flex items-center gap-1">
                <Input 
                  type="date" 
                  value={filters.startDate} 
                  onChange={(e) => setFilters({ ...filters, dateRange: "custom", startDate: e.target.value })} 
                  className="min-w-[140px] w-auto h-9 text-xs px-2" 
                  data-testid="start-date"
                  title="Start Date (dd/mm/yyyy)"
                />
                <span className="text-slate-400 text-xs">to</span>
                <Input 
                  type="date" 
                  value={filters.endDate} 
                  onChange={(e) => setFilters({ ...filters, dateRange: "custom", endDate: e.target.value })} 
                  className="min-w-[140px] w-auto h-9 text-xs px-2" 
                  data-testid="end-date"
                  title="End Date (dd/mm/yyyy)"
                />
              </div>

              <span className="text-xs text-slate-500">{filteredOrders.length} deliveries</span>
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Order</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Customer</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Items / Stores</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Total</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Status</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Delivery Date</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Delivery Address</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600 uppercase text-[10px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-cyan-600 mx-auto" />
                    </td></tr>
                  ) : filteredOrders.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      <ShoppingBag className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>No deliveries found</p>
                    </td></tr>
                  ) : (
                    filteredOrders.map((order) => {
                      const storeTags = [...new Set((order.items || []).map(i => i.store_tag).filter(Boolean))];
                      const deliveryDate = order.delivery_details?.delivery_date || order.delivery_date || "";
                      const deliveryTime = order.delivery_details?.delivery_time || order.delivery_time || "";
                      const deliveryAddress = order.delivery_address || 
                        `${order.delivery_details?.address || ""}, ${order.delivery_details?.suburb || ""} ${order.delivery_details?.postcode || ""}`;
                      
                      return (
                        <tr key={order.id} className="hover:bg-slate-50/50" data-testid={`order-row-${order.id}`}>
                          <td className="px-3 py-2.5">
                            <span className="font-mono text-xs font-semibold text-slate-900">
                              {order.order_number || `BC-${order.id?.slice(0, 6).toUpperCase()}`}
                            </span>
                            <br />
                            <span className="text-[10px] text-slate-400">
                              {order.created_at ? format(parseISO(order.created_at), "HH:mm") : ""}
                            </span>
                          </td>
                          {/* Customer column - show name and contact */}
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-slate-900 text-xs">{order.customer_name || "N/A"}</p>
                            {(order.customer_phone || order.phone) && (
                              <a href={`tel:${order.customer_phone || order.phone}`} className="text-sky-600 hover:text-sky-800 flex items-center gap-0.5 mt-0.5">
                                <Phone className="w-2.5 h-2.5" /><span className="text-[10px]">{order.customer_phone || order.phone}</span>
                              </a>
                            )}
                            <p className="text-[10px] text-slate-400 truncate max-w-[120px]">{order.email}</p>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="text-xs font-medium">{order.items?.length || order.item_count || 0} items</span>
                            <div className="flex gap-0.5 mt-1 flex-wrap">
                              {storeTags.map(tag => <StoreTag key={tag} tag={tag} />)}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-semibold text-xs">${order.total?.toFixed(2)}</td>
                          <td className="px-3 py-2.5"><StatusBadge status={order.order_status} /></td>
                          <td className="px-3 py-2.5 text-xs text-slate-500">
                            {deliveryDate && (
                              <div className="text-sky-600">
                                <Calendar className="w-2.5 h-2.5 inline mr-0.5" />
                                {formatDeliveryDate(deliveryDate)}{deliveryTime ? `, ${deliveryTime}` : ""}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            <div className="flex items-start gap-1">
                              <MapPin className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                              <span className="text-[10px] max-w-[150px]">{deliveryAddress}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1">
                              {/* View Order with Tooltip */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-7 w-7 p-0" 
                                    onClick={() => openOrderDetail(order)} 
                                    data-testid={`view-order-${order.id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View Order</p>
                                </TooltipContent>
                              </Tooltip>

                              {/* Status Update - Delivered only */}
                              <Select 
                                value={order.order_status} 
                                onValueChange={(v) => updateOrderStatus(order.order_number || order.id, v)}
                              >
                                <SelectTrigger className="w-[120px] h-7 text-[10px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ready_for_delivery">Ready for Delivery</SelectItem>
                                  <SelectItem value="delivered">Delivered</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Order Detail Modal - Full view like Founders Portal */}
          {selectedOrder && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-slate-900">
                      {selectedOrder.order_number || `BC-${selectedOrder.id?.slice(0, 6).toUpperCase()}`}
                    </h2>
                    <MemberBadge isMember={selectedOrder.is_member_user} />
                    <StatusBadge status={selectedOrder.order_status} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(null)}>
                    <XCircle className="w-5 h-5" />
                  </Button>
                </div>

                <div className="p-5 space-y-5">
                  {/* Status Row */}
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <p className="text-[10px] text-slate-500 mb-0.5">Order Status</p>
                      <StatusBadge status={selectedOrder.order_status} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 mb-0.5">Payment</p>
                      <PaymentBadge status={selectedOrder.payment_status} transactionId={selectedOrder.transaction_id} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 mb-0.5">Date</p>
                      <span className="text-xs">
                        {selectedOrder.created_at ? format(parseISO(selectedOrder.created_at), "dd MMM yyyy, HH:mm") : "-"}
                      </span>
                    </div>
                  </div>

                  {/* Customer Info - Full Details like Founders Portal */}
                  <div>
                    <h3 className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1">
                      <User className="w-3.5 h-3.5" /> Customer
                    </h3>
                    <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                      <div className="flex items-center gap-4">
                        <span><strong>Name:</strong> {selectedOrder.customer_name || "N/A"}</span>
                        {(selectedOrder.customer_phone || selectedOrder.phone) && (
                          <a href={`tel:${selectedOrder.customer_phone || selectedOrder.phone}`} className="text-sky-600 hover:underline flex items-center gap-1">
                            <Phone className="w-3 h-3" />{selectedOrder.customer_phone || selectedOrder.phone}
                          </a>
                        )}
                      </div>
                      <p className="flex items-center gap-1">
                        <Mail className="w-3 h-3 text-slate-400" />
                        {selectedOrder.email || "N/A"}
                      </p>
                      <p className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {selectedOrder.delivery_details?.address}, {selectedOrder.delivery_details?.suburb} {selectedOrder.delivery_details?.postcode} {selectedOrder.delivery_details?.state}
                      </p>
                      <p>
                        <strong>Delivery:</strong> {formatDeliveryDate(selectedOrder.delivery_details?.delivery_date)} at {selectedOrder.delivery_details?.delivery_time}
                      </p>
                      {selectedOrder.delivery_details?.special_instructions && (
                        <p className="bg-yellow-50 p-2 rounded border border-yellow-100">
                          <strong>Instructions:</strong> {selectedOrder.delivery_details.special_instructions}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Items by Store */}
                  <div>
                    <h3 className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1">
                      <Package className="w-3.5 h-3.5" /> Items ({selectedOrder.items?.length})
                    </h3>
                    <div className="space-y-1.5 max-h-52 overflow-y-auto">
                      {selectedOrder.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-slate-50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <StoreTag tag={item.store_tag} />
                            <div>
                              <p className="text-xs font-medium">
                                {item.url ? (
                                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-800 hover:underline" data-testid={`driver-item-link-${idx}`}>
                                    {item.product_name}
                                  </a>
                                ) : (
                                  item.product_name
                                )}
                              </p>
                              <p className="text-[10px] text-slate-500">
                                {item.store_name || item.store || item.retailer}
                              </p>
                              <p className="text-[10px] text-slate-400">Qty: {item.quantity} × ${item.price?.toFixed(2)}</p>
                            </div>
                          </div>
                          <p className="text-xs font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Financial Breakdown - No fee details for drivers */}
                  <div className="border-t border-slate-200 pt-4">
                    <h3 className="text-xs font-semibold text-slate-900 mb-2 flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5" /> Order Total
                    </h3>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between text-base font-bold">
                        <span>Gross Total</span>
                        <span>${selectedOrder.total?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions - No Save Reconciliation Button */}
                  <div className="flex gap-2 border-t border-slate-200 pt-4">
                    {selectedOrder.order_status === "ready_for_delivery" && (
                      <Button 
                        size="sm" 
                        onClick={() => { 
                          updateOrderStatus(selectedOrder.order_number || selectedOrder.id, "delivered"); 
                          setSelectedOrder(null); 
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="mark-delivered-btn"
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />Mark as Delivered
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setSelectedOrder(null)}>Close</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default DriverPortal;
